import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useContext } from "react";
import { TextField, Label, Stack, Text } from "@fluentui/react";
import { Selection } from "@fluentui/react";
import { css } from "@emotion/css";
import { ISLBatchHeader, ISLBatchGridItem } from "../../schemas/SLBatchGridSchema";
import { getBatchEntryHeaders } from "./SLBatchRequests";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import SLPageOverlay, { ISLPageOverlay } from "../SLPageOverlay";
import { AsyncSubject } from "rxjs";
import SLEntriesDetails from "../SLEntriesDetails";
import { UserContext } from "../../contexts/UserContext";

const batchPopupStyles = css`
  font-size: inherit;
  font-weight: 500;
  color: var(--main-bg-color, gray);
  outline: currentcolor none medium;
  text-decoration: none;
`;

interface IBatchEntriesHeaderSummary {
  batchEntryKey: string;
  isClosed: boolean;
}

interface IBatchCounts {
  success: number;
  failed: number;
}

const countInitialState: IBatchCounts = {
  success: 0,
  failed: 0
};

enum BatchPopupLoadingStatus {
  Idle = "Idle",
  Pending = "Pending",
  Resolved = "Resolved",
  Rejected = "Rejected"
}

const BatchEntriesHeaderSummary = (props: IBatchEntriesHeaderSummary) => {
  const { accessToken } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState<BatchPopupLoadingStatus>(BatchPopupLoadingStatus.Idle);
  const [BatchName, setBatchName] = useState<ISLBatchHeader["batchName"]>("");
  const [BatchDescription, setBatchDescription] = useState<ISLBatchHeader["batchDescription"]>("");
  const [BatchStatus, setBatchStatus] = useState<ISLBatchHeader["batchStatus"]>(0);
  const [BatchDetails, setBatchDetails] = useState<ISLBatchGridItem[]>([]);
  const [BatchCounts, setBatchCounts] = useState<IBatchCounts>(countInitialState);

  useEffect(() => {
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    // Component did mount and component will mount (both)
    let isMounted = true;
    const loadData = async () => {
      try {
        setIsLoading(BatchPopupLoadingStatus.Pending);
        const items = await getBatchEntryHeaders([props.batchEntryKey], "", accessToken);
        const item = items[0];
        if (isMounted) {
          setBatchDetails(item.slEntries);
          setBatchName(item.batchHeader.batchName);
          setBatchCounts({
            success: item.batchHeader.successfulPosting || 0,
            failed: item.batchHeader.failedPosting || 0
          });
          setBatchDescription(item.batchHeader.batchDescription);
          setBatchStatus(item.batchHeader.batchStatus);
          setIsLoading(BatchPopupLoadingStatus.Resolved);
        }
      } catch (e) {
        // do something depending on the error type
        if (isMounted) {
          setIsLoading(BatchPopupLoadingStatus.Rejected);
        }
      }
    };
    if (props.isClosed === false && isLoading) {
      loadData();
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isClosed]);

  if (isLoading === BatchPopupLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading Batch Details" />;
  }

  if (isLoading === BatchPopupLoadingStatus.Rejected) {
    return (
      <Stack className={batchPopupStyles} tokens={{ childrenGap: 9 }} verticalAlign="center">
        <Text
          style={{
            color: "var(--accent-font-color, gray)",
            textAlign: "center"
          }}
        >
          Could not retrieve Batch Details.
        </Text>
      </Stack>
    );
  }

  return (
    <>
      <Stack className={batchPopupStyles} tokens={{ childrenGap: 9 }} verticalAlign="center">
        <Stack horizontal disableShrink tokens={{ childrenGap: 18 }}>
          <Stack.Item grow={2}>
            <Label>Name</Label> <TextField disabled defaultValue={BatchName} />
          </Stack.Item>
          <Stack.Item>
            <Label>Successfully Posted</Label> <TextField disabled defaultValue={`${BatchCounts.success}`} />
          </Stack.Item>
          <Stack.Item>
            <Label>Failed during Posting</Label> <TextField disabled defaultValue={`${BatchCounts.failed}`} />
          </Stack.Item>
        </Stack>
        <Label>Description</Label> <TextField disabled defaultValue={BatchDescription} />
        <Label>Status</Label> <TextField disabled defaultValue={`${BatchStatus}`} />
        {BatchDetails.length !== 0 ? (
          <SLEntriesDetails
            items={BatchDetails}
            selection={new Selection()}
            processingStatus={{}}
            batchPopup={null}
            newTab={true}
            height={350}
            routeState={() => undefined}
          />
        ) : (
          <Text
            style={{
              color: "var(--accent-font-color, gray)",
              textAlign: "center"
            }}
          >
            Batch has no Single Journal Entries.
          </Text>
        )}
      </Stack>
    </>
  );
};

export interface ISLBatchPopupOverlay {
  props: Record<string, unknown>;
  ref: {
    open: (theBatchRefGuid: string) => void;
    getOnCloseAsyncSubject: () => AsyncSubject<boolean>;
  };
}

let onCloseTrigger: null | AsyncSubject<boolean> = null;

const SLBatchPopupOverlay = forwardRef(
  (props: ISLBatchPopupOverlay["props"], ref: React.Ref<ISLBatchPopupOverlay["ref"]>) => {
    const slPageOverlayRef = useRef<ISLPageOverlay["ref"]>(null);
    const [isClosed, setIsClosed] = useState<boolean>(true);
    const [batchRefGuid, setGuid] = useState<string>("");

    useImperativeHandle(ref, (): ISLBatchPopupOverlay["ref"] => ({
      open(theBatchRefGuid) {
        setGuid(theBatchRefGuid);
        openDetails();
      },
      getOnCloseAsyncSubject() {
        onCloseTrigger = new AsyncSubject();
        onCloseTrigger.next(true);
        return onCloseTrigger;
      }
    }));

    const openDetails = () => {
      setIsClosed(false);
      if (slPageOverlayRef.current) {
        slPageOverlayRef.current.open();
      }
    };

    const onCloseOverlay = () => {
      setGuid("");
      if (onCloseTrigger && !onCloseTrigger.closed) {
        onCloseTrigger.complete();
        onCloseTrigger = null;
      }
    };
    return (
      <React.Fragment>
        <SLPageOverlay ref={slPageOverlayRef} label="Batch Details" onClose={onCloseOverlay}>
          {batchRefGuid.length !== 0 ? (
            <BatchEntriesHeaderSummary batchEntryKey={batchRefGuid} isClosed={isClosed} />
          ) : null}
        </SLPageOverlay>
      </React.Fragment>
    );
  }
);

SLBatchPopupOverlay.displayName = "SLBatchPopupOverlay";

export default SLBatchPopupOverlay;
